import { useCallback, useEffect, useRef, useState } from 'react';

export const DEFAULT_SERVER_RESOURCE_POLL_INTERVAL_MS = 30_000;

export type ServerResourceState<T> = {
  data: T | undefined;
  error: Error | null;
  isLoading: boolean;
  isRefreshing: boolean;
};

const emptyResourceState = <T>(): ServerResourceState<T> => ({
  data: undefined,
  error: null,
  isLoading: true,
  isRefreshing: false,
});
const alwaysPoll = () => true;

/** Hide the previous resource while a new resource identity is loading. */
export function stateForResourceKey<T>(
  currentKey: string,
  requestedKey: string,
  state: ServerResourceState<T>,
): ServerResourceState<T> {
  return currentKey === requestedKey ? state : emptyResourceState<T>();
}

type PollTimer = ReturnType<typeof setTimeout>;

export type ServerResourceEnvironment = {
  cancel: (timer: PollTimer) => void;
  isHidden: () => boolean;
  onVisibilityChange: (listener: () => void) => () => void;
  schedule: (callback: () => void, delay: number) => PollTimer;
};

const browserEnvironment: ServerResourceEnvironment = {
  cancel: (timer) => clearTimeout(timer),
  isHidden: () => document.visibilityState === 'hidden',
  onVisibilityChange: (listener) => {
    document.addEventListener('visibilitychange', listener);
    return () => document.removeEventListener('visibilitychange', listener);
  },
  schedule: (callback, delay) => setTimeout(callback, delay),
};

export function createServerResourceController<T>(input: {
  environment?: ServerResourceEnvironment;
  fetchResource: (signal: AbortSignal) => Promise<T>;
  onState: (state: ServerResourceState<T>) => void;
  pollIntervalMs?: number;
  initialState?: ServerResourceState<T>;
  shouldPoll?: (data: T | undefined) => boolean;
}) {
  const environment = input.environment ?? browserEnvironment;
  const pollIntervalMs =
    input.pollIntervalMs ?? DEFAULT_SERVER_RESOURCE_POLL_INTERVAL_MS;
  const shouldPoll = input.shouldPoll ?? (() => true);
  let stopped = false;
  let inFlight: AbortController | null = null;
  let queued = false;
  let timer: PollTimer | null = null;
  let state =
    input.initialState ??
    ({
      data: undefined,
      error: null,
      isLoading: true,
      isRefreshing: false,
    } satisfies ServerResourceState<T>);

  function emit(next: Partial<ServerResourceState<T>>) {
    state = { ...state, ...next };
    input.onState(state);
  }

  function clearTimer() {
    if (timer === null) return;
    environment.cancel(timer);
    timer = null;
  }

  function schedule() {
    clearTimer();
    if (
      stopped ||
      environment.isHidden() ||
      pollIntervalMs <= 0 ||
      !shouldPoll(state.data)
    )
      return;
    timer = environment.schedule(() => {
      timer = null;
      void request();
    }, pollIntervalMs);
  }

  async function request() {
    if (stopped) return;
    if (environment.isHidden()) {
      queued = true;
      clearTimer();
      return;
    }
    if (inFlight) {
      queued = true;
      return;
    }
    const controller = new AbortController();
    inFlight = controller;
    const hasData = state.data !== undefined;
    emit({
      error: null,
      isLoading: !hasData,
      isRefreshing: hasData,
    });
    try {
      const data = await input.fetchResource(controller.signal);
      if (stopped || controller.signal.aborted) return;
      emit({ data, error: null, isLoading: false, isRefreshing: false });
    } catch (error) {
      if (stopped || controller.signal.aborted) return;
      emit({
        error:
          error instanceof Error ? error : new Error('Resource request failed'),
        isLoading: false,
        isRefreshing: false,
      });
    } finally {
      if (inFlight !== controller) return;
      inFlight = null;
      if (queued && !environment.isHidden() && shouldPoll(state.data)) {
        queued = false;
        void request();
      } else {
        queued = false;
        schedule();
      }
    }
  }

  const unsubscribe = environment.onVisibilityChange(() => {
    if (environment.isHidden()) {
      clearTimer();
      return;
    }
    const hasQueuedRequest = queued;
    queued = false;
    if (!hasQueuedRequest && !shouldPoll(state.data)) return;
    void request();
  });

  return {
    refresh: () => void request(),
    start: () => void request(),
    stop: () => {
      stopped = true;
      queued = false;
      clearTimer();
      unsubscribe();
      inFlight?.abort();
      inFlight = null;
    },
  };
}

export function useServerResource<T>(
  resourceKey: string,
  fetchResource: (signal: AbortSignal) => Promise<T>,
  pollIntervalMs = DEFAULT_SERVER_RESOURCE_POLL_INTERVAL_MS,
  shouldPoll: (data: T | undefined) => boolean = alwaysPoll,
): ServerResourceState<T> & { refresh: () => void } {
  const [state, setState] = useState<ServerResourceState<T>>({
    data: undefined,
    error: null,
    isLoading: true,
    isRefreshing: false,
  });
  const [stateKey, setStateKey] = useState(resourceKey);
  const controllerRef = useRef<ReturnType<
    typeof createServerResourceController<T>
  > | null>(null);

  useEffect(() => {
    setStateKey(resourceKey);
    const controller = createServerResourceController({
      fetchResource,
      initialState: {
        data: undefined,
        error: null,
        isLoading: true,
        isRefreshing: false,
      },
      onState: setState,
      pollIntervalMs,
      shouldPoll,
    });
    controllerRef.current = controller;
    controller.start();
    return () => {
      controller.stop();
      if (controllerRef.current === controller) controllerRef.current = null;
    };
  }, [fetchResource, pollIntervalMs, resourceKey, shouldPoll]);

  const refresh = useCallback(() => {
    controllerRef.current?.refresh();
  }, []);
  return {
    ...stateForResourceKey(stateKey, resourceKey, state),
    refresh,
  };
}
